export const useAzureBlob = () => {
  const MEDIA_MIME_TYPES = {
    gif: 'image/gif',
    mp4: 'video/mp4',
  }

  const getFileExtension = fileName => {
    if (!fileName || typeof fileName !== 'string') return ''
    return fileName.split('.').pop()?.toLowerCase() || ''
  }

  const resolveContentType = file => {
    if (file?.type) return file.type
    const extension = getFileExtension(file?.name)
    return MEDIA_MIME_TYPES[extension] || 'application/octet-stream'
  }

  const isVideoOrGifFile = file => {
    const contentType = resolveContentType(file)
    return contentType.startsWith('video/') || contentType === 'image/gif'
  }

  const trimTrailingSlashes = value => (value || '').replace(/\/+$/, '')
  const trimLeadingSlashes = value => (value || '').replace(/^\/+/, '')

  const buildUploadUrl = ({sasToken, directory, fileName}) => {
    const blobUrl = trimTrailingSlashes(import.meta.env.VITE_BLOB_HOST_URL)
    const normalizedDirectory = trimLeadingSlashes(trimTrailingSlashes(directory))

    return `${blobUrl}/${normalizedDirectory}/${fileName}?${sasToken}`
  }

  const uploadToBlob = async ({sasToken, directory, uploadUrl: directUrl}, file) => {
    if (!file) return
    const uploadUrl =
      directUrl || (sasToken && directory ? buildUploadUrl({sasToken, directory, fileName: file?.name}) : null)
    if (!uploadUrl) return
    const contentType = resolveContentType(file)

    try {
      const response = await fetch(uploadUrl, {
        method: 'PUT',
        headers: {
          'x-ms-blob-type': 'BlockBlob',
          'Content-Type': contentType,
          'x-ms-blob-content-type': contentType,
        },
        body: file,
      })

      if (!response.ok) {
        throw new Error('Upload failed')
      }

      return true
    } catch (error) {
      console.error('Error uploading file:', error)
      return error
    }
  }

  const uploadFileOnBlob = async ({sasToken, directory, uploadUrl}, file) => {
    return uploadToBlob({sasToken, directory, uploadUrl}, file)
  }

  const uploadVideoOrGifOnBlob = async ({sasToken, directory, uploadUrl}, file) => {
    return uploadToBlob({sasToken, directory, uploadUrl}, file)
  }

  const uploadMediaFilesOnBlob = async ({sasToken, directory}, files) => {
    const filesToUpload = Array.from(files || []).filter(Boolean)

    if (!sasToken || !directory) return []
    if (filesToUpload.length === 0) return []

    return Promise.all(
      filesToUpload.map(async file => {
        const uploadMethod = isVideoOrGifFile(file) ? uploadVideoOrGifOnBlob : uploadFileOnBlob
        const result = await uploadMethod({sasToken, directory}, file)
        return {
          file,
          fileName: file.name,
          success: result === true,
          error: result === true ? null : result,
        }
      })
    )
  }

  return {uploadFileOnBlob, uploadVideoOrGifOnBlob, uploadMediaFilesOnBlob}
}
